import DOMPurify from "dompurify";
import ons from "onsenui";
import React from "react";
import {
  Control,
  UseFormGetValues,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import { HRQuestionTemplateId } from "../../messages";
import { HRQuestion } from "../../messages/HR";
import { STRINGS } from "../../strings";
import { translate } from "../../translate";
import { If } from "../Generic/If";
import {
  getPossibleAnswers,
  getQuestionFieldName,
  getQuestionText,
} from "../Onboarding/utils/onboardingUtils";
import SignatureBox from "../SignatureBox/SignatureBox";
import Question from "./Question";
import { OnboardingForm } from "./hooks/useOnboardingForm";

interface Props {
  question: HRQuestion;
  isFirst: boolean;
  showLabel: boolean;
  language: string;
  control: Control;
  isReadOnly: boolean;
  isSignaureDisabled: boolean;
  emailVerificationTemplateId: HRQuestionTemplateId | null;
  register: UseFormRegister<OnboardingForm>;
  setValue: UseFormSetValue<OnboardingForm>;
  getValues: UseFormGetValues<OnboardingForm>;
  watch: UseFormWatch<OnboardingForm>;
  onError: (params: { title: string; message: string }) => void;
}

function OnboardingQuestion(props: Props) {
  const name = getQuestionFieldName(props.question);
  const questionText = getQuestionText(props.question, props.language);

  switch (props.question.type) {
    case "Text":
      return (
        <Question.Input
          {...props.register(name)}
          placeholder={props.showLabel ? questionText : translate(STRINGS.ENTER_TEXT_HERE)}
          autoFocus={props.isFirst}
          disabled={props.isReadOnly}
        />
      );

    case "SSN":
      return (
        <Question.SSN
          control={props.control}
          name={name}
          autoFocus={props.isFirst}
          disabled={props.isReadOnly}
        />
      );

    case "PhoneNumber":
      return (
        <Question.PhoneNumber
          control={props.control}
          name={name}
          autoFocus={props.isFirst}
          disabled={props.isReadOnly}
        />
      );

    case "Email": {
      return (
        <>
          {props.emailVerificationTemplateId === props.question.textId ? (
            <Question.Label>{translate(STRINGS.EMAIL_VERIFICATION_QUESTION_LABEL)}</Question.Label>
          ) : null}
          <Question.Input
            {...props.register(name)}
            type="email"
            placeholder={props.showLabel ? questionText : "example@example.com"}
            autoFocus={props.isFirst}
            disabled={props.isReadOnly}
          />
        </>
      );
    }
    case "PlainText": {
      const htmlText = questionText.replace(
        /(https?:\/\/)([^ ]+)/g,
        `<a target="_blank" href="$&">$2</a>`
      );

      return (
        <Question.Paragraph dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(htmlText) }} />
      );
    }

    case "Radio":
      return (
        <Question.Section>
          <If condition={props.showLabel}>
            <Question.Label>{questionText}</Question.Label>
          </If>
          {getPossibleAnswers(props.question, props.language).map((option) => (
            <Question.Radio
              key={option.id}
              {...props.register(name)}
              value={option.id.toString()}
              disabled={props.isReadOnly}
            >
              {option.text}
            </Question.Radio>
          ))}
        </Question.Section>
      );

    case "Checkbox":
      return (
        <Question.Section>
          {getPossibleAnswers(props.question, props.language).map((option) => (
            <Question.Checkbox
              key={option.id}
              {...props.register(name)}
              value={option.id.toString()}
              disabled={props.isReadOnly}
            >
              {option.text}
            </Question.Checkbox>
          ))}
        </Question.Section>
      );

    case "DropDown":
      return (
        <Question.Section>
          <Question.Select {...props.register(name)} defaultValue={""} disabled={props.isReadOnly}>
            <Question.Select.Option disabled={true} value="">
              {props.showLabel ? questionText : translate(STRINGS.SELECT)}
            </Question.Select.Option>

            {getPossibleAnswers(props.question, props.language).map((option) => (
              <Question.Select.Option key={option.id} value={option.id}>
                {option.text}
              </Question.Select.Option>
            ))}
          </Question.Select>
        </Question.Section>
      );

    case "Numeric":
    case "Dollar":
      return (
        <Question.Input
          {...props.register(name)}
          placeholder={props.showLabel ? questionText : translate(STRINGS.ENTER_NUMBER_HERE)}
          autoFocus={props.isFirst}
          type="number"
          disabled={props.isReadOnly}
        />
      );

    case "Signature":
      return (
        <Question.Section>
          <SignatureBox
            placeholder={translate(STRINGS.SIGN_HERE)}
            initialData={props.getValues(name)?.toString()}
            isDisabled={props.isReadOnly || props.isSignaureDisabled}
            onSubmit={(data) => props.setValue(name, data)}
          />
        </Question.Section>
      );

    case "FullDate":
    case "BirthDate":
      return (
        <Question.Date
          {...props.register(name)}
          placeholder={ons.platform.isIPhone() ? translate(STRINGS.MM_DD_YYYY) : questionText}
          disabled={props.isReadOnly}
        />
      );

    case "Placeholder":
      return (
        <Question.Section>
          <Question.Placeholder src={questionText} alt="placeholder" />
        </Question.Section>
      );

    case "Video":
      return (
        <Question.Section>
          <Question.Video videoUrl={questionText} />
        </Question.Section>
      );
    case "Interview":
      return (
        <Question.Section>
          <If condition={props.showLabel}>
            <Question.Label>{questionText}</Question.Label>
          </If>
          {props.question.answer !== null && props.question.chosenAnswerId === null ? (
            <Question.Radio
              key={props.question.answer}
              value={props.question.answer}
              disabled={props.isReadOnly}
              checked={true}
            >
              {props.question.answer}
            </Question.Radio>
          ) : (
            getPossibleAnswers(props.question, props.language).map((option) => (
              <Question.Radio
                key={option.id}
                {...props.register(name)}
                value={option.id.toString()}
                disabled={props.isReadOnly}
              >
                {option.text}
              </Question.Radio>
            ))
          )}
        </Question.Section>
      );

    case "Document": {
      const rejectReason =
        props.question.complianceRejectionData?.type === "Rejected" &&
        props.watch(name)?.toString().startsWith("https://") === true
          ? props.question.complianceRejectionData.reason
          : undefined;

      return (
        <Question.Section>
          <If condition={rejectReason !== undefined}>
            <Question.ErrorMessage>
              {translate(STRINGS.DOCUMENT_REJECTED)}. <br /> {translate(STRINGS.REASON)}:{" "}
              {rejectReason}
            </Question.ErrorMessage>
          </If>

          <If condition={props.showLabel}>
            <Question.Label>{questionText}</Question.Label>
          </If>

          <p>{translate(STRINGS.MAKE_SURE_PICTURE_IS_CLEAR)}</p>

          <Question.Document
            fieldName={getQuestionFieldName(props.question)}
            setValue={props.setValue}
            initialData={props.getValues(name)?.toString()}
            isDisabled={props.isReadOnly}
            canUploadFromGallery={props.question.canUploadFromGallery}
            isSelfie={props.question.textId.toString() === "GeneralSelfie_medflyt"}
            onClickDisabled={() =>
              props.onError({
                title: translate(STRINGS.WHOOPS),
                message: translate(STRINGS.CANNOT_EDIT_ANSWERS_IN_READ_ONLY_MODE),
              })
            }
          />
        </Question.Section>
      );
    }
    case "StartAndEndTime":
    case "Star":
    case "DocumentWithExpiry":
    case "DownloadableFile":
    case "Address":
    case "MonthYearDate":
    case "YearDate":
    case "GeneralSignature":
    case "AmericanTest":
    case "FreeText":
    case "TitleText":
      return <p>Type ${props.question.type} is not implemented.</p>;

    default:
      assumesNever(props.question);
      return (
        <Question.Section>
          <Question.ErrorMessage>
            {translate(
              STRINGS.IN_ORDER_TO_ANSWER_THIS_QUESTION_PLEASE_UPDATE_TO_OUR_NEWEST_VERSION_FROM_THE_STORE
            )}
          </Question.ErrorMessage>
        </Question.Section>
      );
  }
}

function assumesNever(_value: never) {
  return;
}

export default OnboardingQuestion;
